import React, {useEffect, useRef, useState} from 'react'
import {Link as RouteLink} from 'react-router-dom'
import {
    Badge,
    Box,
    IconButton,
    Image,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import {SearchBar} from '../header'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {SearchIcon, AccountIcon, BasketIcon, KsLogo} from '../icons'
import useTagManager from '../../hooks/use-tag-manager'
import {useSiteContext} from '../../contexts/site'
import {getUrl} from '../../utils/cloudinary-utils'
import {getTotalQuantity} from '../../utils/basket-utils'
import {BASKET_URL} from '../../constants'

const StickyBackSection = ({...rest}) => {
    const {onLogoClick, setShowSpinner, onClick, onMyAccountClick, onMyCartClick} = rest
    const intl = useIntl()
    const {isYR} = useSiteContext()
    const styles = useMultiStyleConfig('Header')
    const tagManager = useTagManager()
    const {
        app: {commerceAPI}
    } = getConfig()
    const {changeUp, storefront} = commerceAPI
    const {configChangeup} = changeUp || {}
    const {storefrontPreferences} = storefront || {}
    const pdIconRef = useRef(null)
    let enablePurpleDot = false
    try {
        enablePurpleDot = JSON.parse(commerceAPI.purpleDot?.config || '{}').enabled
    } catch (e) {
        // Keep the navigation available if Purple Dot is not configured.
    }
    const {data: basket} = useCurrentBasket()
    var changeUpProductID = null
    try {
        const jsonConfig = JSON.parse(configChangeup)
        changeUpProductID = jsonConfig.enabled ? jsonConfig.productID : null
    } catch (e) {
        console.log(e.message)
    }

    let composableCartEnabled = false
    try {
        const storefrontJsonConfig = JSON.parse(storefrontPreferences)
        composableCartEnabled = storefrontJsonConfig.enableComposableCart
    } catch (e) {
        console.log(e.message)
    }

    const [totalQuantity, setTotalQuantity] = useState(0)
    const quantityTotal = basket?.productItems?.reduce((s, i) => s + i?.quantity, 0)
    const giftQtyTotal = basket?.giftCertificateItems?.reduce((s, i) => s + i?.quantity, 0)
    useEffect(() => {
        setTotalQuantity(getTotalQuantity(basket, changeUpProductID))
    }, [quantityTotal, giftQtyTotal])
    useEffect(() => {
        if (!enablePurpleDot) return undefined
        const interval = setInterval(() => {
            const count = pdIconRef.current
                ?.querySelector('purple-dot-separate-bag')
                ?.getAttribute('data-item-count')
            if (count && count !== '0') {
                pdIconRef.current?.classList.remove('hide-element')
                clearInterval(interval)
            }
        }, 500)
        return () => clearInterval(interval)
    }, [enablePurpleDot])
    const [showSearchTab, setShowSearchTab] = useState(false)
    return (
        <Box
            className="sticky-back-section"
            display="flex"
            alignItems="center"
            height="44px"
            position="relative"
            width="100%"
        >
            <Box
                className="left-section"
                position="absolute"
                left="50%"
                transform="translateX(-50%)"
            >
                <IconButton
                    aria-label={intl.formatMessage({
                        id: 'header.button.assistive_msg.logo',
                        defaultMessage: 'Logo'
                    })}
                    icon={
                        isYR ? (
                            <Image
                                {...styles.logo}
                                src={getUrl('UIImages/images/small-yr-logo-white.svg')}
                                width="31px"
                                alt="Yellow Rose Logo"
                                height="34px"
                                verticalAlign="middle"
                            />
                        ) : (
                            <KsLogo
                                {...styles.logo}
                                alt="Kendra Scott Logo"
                                width="33px"
                                height="33px"
                                verticalAlign="middle"
                            />
                        )
                    }
                    variant="unstyled"
                    width="40px"
                    height="40px"
                    onClick={onLogoClick}
                />
            </Box>
            <Box
                className="right-section"
                display="flex"
                alignItems="center"
                marginLeft="auto"
                marginRight="0"
                sx={{
                    '@media (min-width: 360px) and (max-width: 391px)': {
                        '> button, > a': {minWidth: '36px', width: '36px', padding: '4px'}
                    },
                    '@media (max-width: 359px)': {
                        '> button, > a': {minWidth: '32px', width: '32px', padding: '4px'}
                    }
                }}
            >
                <Box
                    className="search-menu"
                    width={showSearchTab ? '100%' : '0'}
                    left={showSearchTab ? 0 : '-100%'}
                    {...styles.searchMenu}
                    sx={{
                        backdropFilter: showSearchTab ? {md: 'blur(5px)'} : 'none'
                    }}
                >
                    <SearchBar
                        isSearchDrawer={true}
                        setShowSearchTab={(bool) => {
                            setShowSearchTab(bool)
                        }}
                        id="sticky-back"
                        setShowSpinner={() => {
                            setShowSpinner(true)
                            onClick(false)
                        }}
                        showSearchTab={showSearchTab}
                        isStickyBackSection={true}
                    />
                </Box>
                <IconButton
                    aria-label={intl.formatMessage({
                        id: 'header.button.assistive_msg.search',
                        defaultMessage: 'Search'
                    })}
                    icon={
                        <SearchIcon
                            alt="Search Logo"
                            width="15px"
                            height="15px"
                            verticalAlign="middle"
                            color={isYR ? 'white' : 'black'}
                        />
                    }
                    {...styles.icons.main}
                    {...styles.icons.mobileSearchLogo}
                    margin="0"
                    onClick={() => {
                        tagManager.formFieldChange('simpleSearch', 'Form Submit Intent')
                        setShowSearchTab(!showSearchTab)
                    }}
                    minWidth="40px"
                    height="44px"
                />
                <IconButton
                    icon={
                        <AccountIcon
                            alt="User Icon"
                            width="16px"
                            height="16px"
                            verticalAlign="middle"
                            color={isYR ? 'white' : 'black'}
                        />
                    }
                    aria-label={intl.formatMessage({
                        id: 'header.button.assistive_msg.my_account',
                        defaultMessage: 'My Account'
                    })}
                    variant="unstyled"
                    display={{lg: 'none'}}
                    {...styles.icons.main}
                    margin="0"
                    minWidth="40px"
                    height="44px"
                    onClick={onMyAccountClick}
                />
                <IconButton
                    {...(composableCartEnabled
                        ? {as: RouteLink, to: BASKET_URL}
                        : {onClick: onMyCartClick})}
                    sx={{
                        'z-index': 'auto'
                    }}
                    opacity="1"
                    pointerEvents="auto"
                    className="basket-icon"
                    aria-label={intl.formatMessage(
                        {
                            id: 'header.button.assistive_msg.my_cart_with_num_items',
                            defaultMessage: 'My cart, number of items: {numItems}'
                        },
                        {numItems: totalQuantity}
                    )}
                    icon={
                        <>
                            <BasketIcon
                                alt="Bag icon"
                                width="14px"
                                height="16px"
                                verticalAlign="middle"
                                color={isYR ? 'white' : 'black'}
                            />
                            {basket && totalQuantity > 0 && (
                                <Badge
                                    variant="notification"
                                    className="total-items"
                                    fontSize="9px"
                                    color={isYR ? 'black' : 'white'}
                                    backgroundColor={isYR ? 'white' : 'black'}
                                    top={{base: '12px', lg: '0.18rem'}}
                                    right={{base: '6px', lg: '0.2rem'}}
                                >
                                    {totalQuantity}
                                </Badge>
                            )}
                        </>
                    }
                    variant="unstyled"
                    {...styles.icons.main}
                    margin="0"
                    padding="8px"
                    minWidth="40px"
                    height="44px"
                    display="inline-grid"
                />
                {enablePurpleDot && (
                    <IconButton
                        ref={pdIconRef}
                        className="pd-icon hide-element"
                        aria-label="Purple Dot bag"
                        icon={<purple-dot-separate-bag />}
                        variant="unstyled"
                        width="40px"
                        height="44px"
                        sx={{
                            'purple-dot-separate-bag svg': {
                                width: '16px',
                                height: '20px',
                                stroke: isYR ? 'white' : 'black'
                            },
                            '.purple-dot-separate-bag__indicator': {
                                width: '5px',
                                height: '5px',
                                backgroundColor: isYR ? 'white' : 'purple.90'
                            }
                        }}
                        onClick={() => tagManager.openPdBasket('header')}
                    />
                )}
            </Box>
        </Box>
    )
}

export default StickyBackSection
